import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { AlertCircle, Coins } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Free limits configuration - must match backend
// NOTE: Publishing public events is always free (no limit)
const FREE_LIMITS = {
  private_events: 3,
  groups: 2,
  circles: 1
};

// Show indicator when at or above this % of limit OR within 1 of limit
const SHOW_THRESHOLD_PERCENT = 0.7;

/**
 * Determines if limit indicator should be shown
 * Show when: used >= 70% of limit OR within 1 of limit
 */
const shouldShowIndicator = (current, limit) => {
  if (limit === 0) return false;
  // Within 1 of limit
  if (current >= limit - 1) return true;
  // At or above 70% of limit
  if (current / limit >= SHOW_THRESHOLD_PERCENT) return true;
  return false;
};

/**
 * LimitIndicator - Shows usage/limit for a specific resource
 * Only displays when user is near the limit
 * Informational only - does not block actions
 */
const LimitIndicator = ({ 
  resourceType, 
  current = null, 
  limit = null,
  className = "",
  size = "sm", // "sm" | "default"
  showOnlyAtLimit = false // If true, only show when at limit
}) => {
  const navigate = useNavigate();
  
  // Use provided values or defaults from FREE_LIMITS
  const freeLimit = limit ?? FREE_LIMITS[resourceType] ?? 0;
  const currentCount = current ?? 0;
  
  // Don't show if values not available
  if (current === null || freeLimit === 0) return null;
  
  // Check if should show based on threshold
  if (!shouldShowIndicator(currentCount, freeLimit)) return null;
  
  // If showOnlyAtLimit, only show when at or over limit
  if (showOnlyAtLimit && currentCount < freeLimit) return null;
  
  const remaining = Math.max(0, freeLimit - currentCount);
  const isAtLimit = remaining === 0;
  
  const sizeClasses = {
    sm: "text-xs px-1.5 py-0.5",
    default: "text-sm px-2 py-1"
  };

  return (
    <Badge 
      variant="outline"
      className={`
        ${sizeClasses[size]}
        ${isAtLimit 
          ? 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700' 
          : 'bg-muted/50 text-muted-foreground border-muted'
        }
        ${className}
      `}
      data-testid={`limit-indicator-${resourceType}`}
    >
      {isAtLimit ? (
        <span className="flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          0 free left
        </span>
      ) : (
        <span>{currentCount}/{freeLimit} free</span>
      )}
    </Badge>
  );
};

/**
 * Hook to fetch and manage limit data
 */
export const useLimits = () => {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLimits = useCallback(async () => {
    if (!isAuthenticated) {
      setLimits(null);
      return null;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/credits/limits`, {
        headers: getAuthHeaders()
      });
      setLimits(response.data.limits);
      return response.data.limits;
    } catch (error) {
      console.error('Failed to fetch limits:', error);
      setLimits(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isAuthenticated]);

  // Fetch on mount
  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  return { limits, loading, refreshLimits: fetchLimits };
};

/**
 * LimitIndicators - Container that fetches and displays all limit indicators
 * Shows only for resources near their limit
 */
export const LimitIndicators = ({ className = "" }) => {
  const { limits, loading } = useLimits();
  const navigate = useNavigate();

  if (loading || !limits) return null;

  // Check which limits should be shown
  const indicatorsToShow = Object.entries(limits).filter(([key, data]) => 
    shouldShowIndicator(data.current, data.free_limit)
  );

  if (indicatorsToShow.length === 0) return null;

  const resourceLabels = {
    private_events: 'Private Events',
    groups: 'Groups',
    circles: 'Circles'
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {indicatorsToShow.map(([key, data]) => (
        <button
          key={key}
          onClick={() => navigate('/account?tab=credits')}
          className="flex items-center gap-1.5 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-1 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors border border-amber-200 dark:border-amber-800"
          data-testid={`limit-btn-${key}`}
        >
          <span className="font-medium">{resourceLabels[key]}:</span>
          <span>
            {data.remaining_free === 0 ? (
              <span className="flex items-center gap-0.5">
                <AlertCircle className="h-3 w-3" />
                0 free left
              </span>
            ) : (
              `${data.current}/${data.free_limit}`
            )}
          </span>
        </button>
      ))}
      <button
        onClick={() => navigate('/account?tab=credits')}
        className="text-xs text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1"
        data-testid="get-more-credits-btn"
      >
        <Coins className="h-3 w-3" />
        Get credits
      </button>
    </div>
  );
};

/**
 * QuietLimitIndicators - Neutral, non-promotional limit display
 * Shows only for resources near their limit
 * No warning colors, no CTAs - purely informational
 */
export const QuietLimitIndicators = ({ className = "" }) => {
  const { limits, loading } = useLimits();
  const navigate = useNavigate();

  if (loading || !limits) return null;

  // Check which limits should be shown
  const indicatorsToShow = Object.entries(limits).filter(([key, data]) => 
    shouldShowIndicator(data.current, data.free_limit)
  );

  if (indicatorsToShow.length === 0) return null;

  const resourceLabels = {
    private_events: 'Private',
    groups: 'Groups',
    circles: 'Circles'
  };

  return (
    <div className={`flex flex-wrap items-center gap-3 text-xs text-muted-foreground ${className}`}>
      {indicatorsToShow.map(([key, data]) => (
        <button
          key={key}
          onClick={() => navigate('/account?tab=credits')}
          className="hover:text-foreground transition-colors"
          data-testid={`quiet-limit-${key}`}
        >
          {resourceLabels[key]} · {data.current} of {data.free_limit}
        </button>
      ))}
    </div>
  );
};

export default LimitIndicator;
